import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

const DAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

export default function LeaveDepartment() {
  const { user } = useAuth();
  const [baseDate, setBaseDate] = useState(new Date());
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  const weekDates = getWeekDates(baseDate);
  const weekStart = toDateStr(weekDates[0]);
  const weekEnd = toDateStr(weekDates[6]);

  useEffect(() => {
    if (!user) return;
    fetch('/api/leaves/department').then(r => r.json()).then(setLeaves).catch(() => {});
    fetch('/api/users').then(r => r.json()).then(setUsers).catch(() => {});
  }, [user]);

  const prevWeek = () => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() - 7);
    setBaseDate(d);
  };

  const nextWeek = () => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + 7);
    setBaseDate(d);
  };

  const goToday = () => setBaseDate(new Date());

  // 특정 날짜에 휴가 중인 직원 목록
  const getLeavesOnDate = (dateStr) => {
    return leaves.filter(l => {
      if (l.status !== 'approved' && l.status !== 'pending') return false;
      if (l.dateMode === 'range') {
        return l.rangeStart <= dateStr && dateStr <= l.rangeEnd;
      }
      return l.dates?.some(d => d.date === dateStr);
    });
  };

  // 이번 주에 휴가인 직원 목록 (테이블용)
  const weekLeaves = leaves.filter(l => {
    if (l.status !== 'approved' && l.status !== 'pending') return false;
    const start = l.dateMode === 'range' ? l.rangeStart : l.dates?.[0]?.date;
    const end = l.dateMode === 'range' ? l.rangeEnd : l.dates?.[l.dates?.length - 1]?.date;
    if (!start || !end) return false;
    return start <= weekEnd && end >= weekStart;
  });

  const filtered = weekLeaves.filter(l => {
    if (!search) return true;
    return l.userName?.includes(search) || l.userDept?.includes(search);
  });

  const today = toDateStr(new Date());

  const STATUS_COLOR = { pending: '#f59e0b', approved: '#059669', rejected: '#dc2626' };
  const STATUS_LABEL = { pending: '대기', approved: '승인', rejected: '반려' };

  return (
    <div className="page container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 className="page-title">부서 휴가현황</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={prevWeek} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontWeight: 600, fontSize: 15, minWidth: 200, textAlign: 'center' }}>
            {weekStart} ~ {weekEnd}
          </span>
          <button onClick={nextWeek} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
            <ChevronRight size={16} />
          </button>
          <button onClick={goToday} className="btn btn-secondary btn-sm">오늘</button>
        </div>
      </div>

      {/* 주간 캘린더 */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
          {weekDates.map((date, i) => {
            const dateStr = toDateStr(date);
            const isToday = dateStr === today;
            const isWeekend = i >= 5;
            const dayLeaves = getLeavesOnDate(dateStr);
            const count = dayLeaves.length;

            return (
              <div key={dateStr} style={{ minHeight: 120 }}>
                {/* 날짜 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, padding: '6px 8px',
                  background: isToday ? '#4f46e5' : isWeekend ? '#f9fafb' : '#f3f4f6', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: isToday ? '#fff' : isWeekend ? '#9ca3af' : '#374151' }}>
                    {date.getDate()} {DAY_LABELS[i]}
                  </span>
                  {count > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, background: isToday ? 'rgba(255,255,255,0.3)' : '#4f46e5', color: '#fff', padding: '1px 7px', borderRadius: 10 }}>
                      {count}명
                    </span>
                  )}
                </div>
                {/* 휴가자 목록 */}
                <div style={{ padding: '0 4px' }}>
                  {dayLeaves.slice(0, 3).map((l, idx) => (
                    <div key={idx} style={{ fontSize: 11, padding: '3px 6px', marginBottom: 3, borderRadius: 4, background: '#eff6ff', color: '#1e40af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.userName} ({l.leaveType})
                    </div>
                  ))}
                  {dayLeaves.length > 3 && (
                    <div style={{ fontSize: 11, color: '#9ca3af', padding: '2px 6px' }}>
                      + 그 외 {dayLeaves.length - 3}명
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 검색 */}
      <div style={{ marginBottom: 14 }}>
        <input className="form-input" style={{ maxWidth: 300 }}
          placeholder="부서, 사원명을 검색하세요..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* 휴가 목록 테이블 */}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>사번</th>
                <th>사원명</th>
                <th>부서명</th>
                <th>휴가형</th>
                <th>휴가사용일</th>
                <th>사용휴가</th>
                <th>휴가사용기간</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>이번 주 휴가자가 없습니다</td></tr>
                : filtered.map((l, i) => {
                  const u = users.find(u => String(u.id) === String(l.userId));
                  const dateRange = l.dateMode === 'range'
                    ? `${l.rangeStart} ~ ${l.rangeEnd}`
                    : l.dateRange || '-';
                  const usageDays = l.dateMode === 'range'
                    ? `${l.totalDays}d`
                    : `${l.totalDays}d`;

                  return (
                    <tr key={l.id}>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{u?.id || '-'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{l.userName?.[0]}</div>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{l.userName}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>{l.userDept || '-'}</td>
                      <td style={{ fontSize: 13 }}>{l.leaveType}</td>
                      <td style={{ fontSize: 13 }}>
                        {l.dateMode === 'pick' && l.dates?.length > 0
                          ? l.dates.map(d => d.date).join(', ')
                          : dateRange}
                      </td>
                      <td style={{ fontSize: 13 }}>{usageDays} ({(l.totalDays * 8).toFixed(0)}h 0m)</td>
                      <td style={{ fontSize: 13 }}>{dateRange}</td>
                      <td>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                          background: l.status === 'approved' ? '#d1fae5' : l.status === 'pending' ? '#fef3c7' : '#fee2e2',
                          color: STATUS_COLOR[l.status] || '#374151' }}>
                          {STATUS_LABEL[l.status] || l.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
